import React, { useState, useEffect } from 'react';
import { useI18n } from '../../i18n';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Database,
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  FileWarning,
  Bug,
  Server,
  Activity,
  Calendar,
  HardDrive,
  Loader2,
  ChevronRight,
  Download,
  Eye
} from 'lucide-react';
import apiClient from '../../api/client';

const AntivirusDashboard = () => {
  const { t } = useI18n();
  const [clamavStatus, setClamavStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [quarantine, setQuarantine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [reloadingSignatures, setReloadingSignatures] = useState(false);
  const [selectedDays, setSelectedDays] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [selectedDays]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusRes, statsRes, quarantineRes] = await Promise.all([
        apiClient.get('/antivirus/status'),
        apiClient.get('/antivirus/admin/statistics', { params: { days: selectedDays } }),
        apiClient.get('/antivirus/admin/quarantine', { params: { limit: 50 } })
      ]);

      setClamavStatus(statusRes.data);
      setStatistics(statsRes.data);
      setQuarantine(quarantineRes.data.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async () => {
    setRefreshing(true);
    try {
      const response = await apiClient.get('/antivirus/status');
      setClamavStatus(response.data);
    } catch (err) {
      console.error('Error refreshing status:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const reloadSignatures = async () => {
    setReloadingSignatures(true);
    try {
      await apiClient.post('/antivirus/admin/reload-signatures');
      // Wait a bit and refresh status
      setTimeout(refreshStatus, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setReloadingSignatures(false);
    }
  };

  const cleanupQuarantine = async () => {
    setCleaningUp(true);
    try {
      await apiClient.post('/antivirus/admin/cleanup-quarantine');
      // Refresh quarantine list
      const response = await apiClient.get('/antivirus/admin/quarantine', { params: { limit: 50 } });
      setQuarantine(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setCleaningUp(false);
    }
  };

  const deleteQuarantineEntry = async (entryId) => {
    if (!window.confirm(t('antivirus.confirmDelete', 'Are you sure you want to permanently delete this file?'))) {
      return;
    }

    try {
      await apiClient.delete(`/antivirus/admin/quarantine/${entryId}`);
      setQuarantine(prev => prev.filter(e => e.id !== entryId));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-red-400 bg-red-500/20 border-red-500/30',
      high: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
      medium: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30',
      low: 'text-blue-400 bg-blue-500/20 border-blue-500/30'
    };
    return colors[severity] || 'text-gray-400 bg-gray-500/20 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Shield className="w-8 h-8 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{t('antivirus.adminTitle', 'Antivirus Management')}</h1>
            <p className="text-gray-400">{t('antivirus.adminSubtitle', 'ClamAV monitoring and quarantine management')}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value={7}>7 {t('common.days', 'days')}</option>
            <option value={30}>30 {t('common.days', 'days')}</option>
            <option value={90}>90 {t('common.days', 'days')}</option>
            <option value={365}>1 {t('common.year', 'year')}</option>
          </select>
          <button
            onClick={loadDashboardData}
            disabled={loading}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* ClamAV Status Card */}
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Server className="w-5 h-5 text-purple-400" />
            ClamAV {t('common.status', 'Status')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={reloadSignatures}
              disabled={reloadingSignatures}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white rounded-lg flex items-center gap-2 text-sm"
            >
              {reloadingSignatures ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {t('antivirus.reloadSignatures', 'Reload Signatures')}
            </button>
            <button
              onClick={refreshStatus}
              disabled={refreshing}
              className="p-2 text-gray-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Status */}
          <div className={`p-4 rounded-lg border ${
            clamavStatus?.is_available
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {clamavStatus?.is_available ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              <span className={clamavStatus?.is_available ? 'text-green-400' : 'text-red-400'}>
                {clamavStatus?.is_available ? t('common.online', 'Online') : t('common.offline', 'Offline')}
              </span>
            </div>
            <p className="text-sm text-gray-400">{t('antivirus.daemonStatus', 'Daemon Status')}</p>
          </div>

          {/* Version */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <p className="text-xl font-semibold text-white">{clamavStatus?.version || '-'}</p>
            <p className="text-sm text-gray-400">{t('antivirus.version', 'Version')}</p>
          </div>

          {/* Signature Version */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <p className="text-xl font-semibold text-white">{clamavStatus?.signature_version || '-'}</p>
            <p className="text-sm text-gray-400">{t('antivirus.signatureVersion', 'Signature Version')}</p>
          </div>

          {/* Signature Count */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <p className="text-xl font-semibold text-white">
              {clamavStatus?.signature_count?.toLocaleString() || '-'}
            </p>
            <p className="text-sm text-gray-400">{t('antivirus.signatureCount', 'Signatures')}</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Scans */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{statistics.total_scans}</p>
                <p className="text-sm text-gray-400">{t('antivirus.totalScans', 'Total Scans')}</p>
              </div>
            </div>
          </div>

          {/* Clean Files */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <ShieldCheck className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-green-400">{statistics.by_status?.clean || 0}</p>
                <p className="text-sm text-gray-400">{t('antivirus.cleanFiles', 'Clean Files')}</p>
              </div>
            </div>
          </div>

          {/* Infected Files */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <ShieldAlert className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-red-400">{statistics.by_status?.infected || 0}</p>
                <p className="text-sm text-gray-400">{t('antivirus.infectedFiles', 'Infected Files')}</p>
              </div>
            </div>
          </div>

          {/* Data Scanned */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <HardDrive className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{statistics.total_mb_scanned?.toFixed(1) || 0} MB</p>
                <p className="text-sm text-gray-400">{t('antivirus.dataScanned', 'Data Scanned')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Severity Breakdown & Performance */}
      {statistics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Severity Breakdown */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Bug className="w-5 h-5 text-red-400" />
              {t('antivirus.threatsBySeverity', 'Threats by severity')}
            </h3>
            <div className="space-y-3">
              {['critical', 'high', 'medium', 'low'].map(severity => (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getSeverityColor(severity)}`}>
                      {severity}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${
                          severity === 'critical' ? 'bg-red-500' :
                          severity === 'high' ? 'bg-orange-500' :
                          severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'
                        }`}
                        style={{
                          width: `${statistics.by_severity?.[severity]
                            ? (statistics.by_severity[severity] / statistics.total_scans * 100)
                            : 0}%`
                        }}
                      />
                    </div>
                    <span className="text-white font-medium w-8 text-right">
                      {statistics.by_severity?.[severity] || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Stats */}
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-400" />
              {t('antivirus.performance', 'Performance')}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">
                  {statistics.average_scan_time_ms?.toFixed(0) || 0}ms
                </p>
                <p className="text-sm text-gray-400">{t('antivirus.avgScanTime', 'Avg Scan Time')}</p>
              </div>
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <p className="text-2xl font-bold text-white">{statistics.cache_entries || 0}</p>
                <p className="text-sm text-gray-400">{t('antivirus.cacheEntries', 'Cache Entries')}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quarantine Section */}
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileWarning className="w-5 h-5 text-yellow-400" />
            {t('antivirus.quarantine', 'Quarantine')} ({quarantine.length})
          </h2>
          <button
            onClick={cleanupQuarantine}
            disabled={cleaningUp}
            className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30 rounded-lg flex items-center gap-2"
          >
            {cleaningUp ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {t('antivirus.cleanupExpired', 'Cleanup Expired')}
          </button>
        </div>

        {quarantine.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-green-400/50" />
            <p>{t('antivirus.noQuarantine', 'No files in quarantine')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="pb-3 font-medium">{t('antivirus.filename', 'Filename')}</th>
                  <th className="pb-3 font-medium">{t('antivirus.threat', 'Threat')}</th>
                  <th className="pb-3 font-medium">{t('antivirus.severity', 'Severity')}</th>
                  <th className="pb-3 font-medium">{t('antivirus.size', 'Size')}</th>
                  <th className="pb-3 font-medium">{t('antivirus.quarantinedAt', 'Quarantined')}</th>
                  <th className="pb-3 font-medium">{t('antivirus.expiresAt', 'Expires')}</th>
                  <th className="pb-3 font-medium">{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {quarantine.map((entry) => (
                  <tr key={entry.id} className="text-gray-300 hover:bg-gray-800/50">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <Bug className="w-4 h-4 text-red-400" />
                        <span className="truncate max-w-[200px]">{entry.original_filename}</span>
                      </div>
                    </td>
                    <td className="py-3 text-red-400">{entry.threat_name}</td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getSeverityColor(entry.severity)}`}>
                        {entry.severity}
                      </span>
                    </td>
                    <td className="py-3 text-gray-400">{formatFileSize(entry.file_size)}</td>
                    <td className="py-3 text-gray-400">
                      {new Date(entry.quarantined_at).toLocaleString()}
                    </td>
                    <td className="py-3 text-gray-400">
                      {new Date(entry.expires_at).toLocaleString()}
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => deleteQuarantineEntry(entry.id)}
                        className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title={t('common.delete', 'Delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AntivirusDashboard;
